/**
 * Billing API - Stripe subscription management (single source of truth)
 *
 * All billing-related API calls and types live here.
 * SettingsService re-exports these for backward compatibility.
 */
import { http } from './api'
import { PAGINATION } from '@/config/constants'

// ─── Raw backend types ───────────────────────────────────────────────

export type PlanTier = 'free' | 'starter' | 'growth' | 'enterprise'

export interface SubscriptionInfo {
  plan_tier: PlanTier
  subscription_status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'paused'
  trial_ends_at: string | null
  current_period_end: string | null
  seats_purchased: number
}

export interface UsageInfo {
  current: number
  limit: number | 'Unlimited'
  percentage: number
}

export interface PlanLimits {
  plan_tier: string
  subscription_status: string
  trial_ends_at: string | null
  current_period_end: string | null
  requires_byom: boolean
  llm_provider: 'byom' | 'managed'
  usage: {
    prospects: UsageInfo
    users: UsageInfo
    sequences: UsageInfo
    ai_outreach_monthly: UsageInfo
    skill_executions_daily: UsageInfo
    chat_messages_daily: UsageInfo
  }
  features: {
    intent_scoring: string
    network_graph: string
    attribution_days: number
    sso_enabled: boolean
    api_access: boolean
    custom_integrations: boolean
    support_level: string
  }
}

export interface CreateCheckoutResponse {
  success: boolean
  checkout_url?: string
  message?: string
}

export interface CreatePortalResponse {
  success: boolean
  portal_url: string
}

export interface StripeConfig {
  stripe_enabled: boolean
  publishable_key: string
  price_id_starter: string | null
  price_id_growth: string | null
  price_id_enterprise: string | null
  trial_period_days?: number
}

// ─── UI-facing types (consumed by BillingSection / SubscriptionManager) ──

export interface BillingPlan {
  id: string
  name: string
  price: number
  display_price?: string
  interval: string
  per_seat?: boolean
  seats_included?: number
  extra_seat_price?: number
  yearly_price?: number | null
  yearly_display_price?: string | null
  yearly_extra_seat_price?: number | null
  description?: string
  cta?: string
  disabled?: boolean
  is_preview?: boolean
  includes_from?: string
  features: string[]
  limits?: {
    prospects: number | 'unlimited'
    campaigns: number | 'unlimited'
    team_members: number
    api_calls_per_month: number | 'unlimited'
  }
}

export interface PricingExperimentInfo {
  enabled: boolean
  variant: string
  variant_label?: string
  default_variant: string
  available_variants: string[]
}

export interface AvailablePlansResponse {
  plans: BillingPlan[]
  currency?: string
  trial_period_days?: number
  enterprise_enabled?: boolean
  marketing_copy?: Record<string, string>
  experiment?: PricingExperimentInfo
}

export interface CurrentSubscription {
  id: string | null
  plan: BillingPlan
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'free'
  current_period_start?: string
  current_period_end: string
  cancel_at_period_end: boolean
  trial_end?: string
}

export interface UsageMetrics {
  prospects_contacted: number
  prospects_limit: number | 'unlimited'
  campaigns_active: number
  campaigns_limit: number | 'unlimited'
  team_members_count: number
  team_members_limit: number
  api_calls_this_month: number
  api_calls_limit: number | 'unlimited'
  storage_used_mb: number
  storage_limit_mb: number
  skill_executions_today: number
  skill_executions_daily_limit: number | 'unlimited'
  chat_messages_today: number
  chat_messages_daily_limit: number | 'unlimited'
}

export interface Invoice {
  id: string
  number: string
  amount_due: number
  amount_paid: number
  currency: string
  status: 'paid' | 'open' | 'void' | 'uncollectible'
  created: string
  period_start?: string
  period_end?: string
  hosted_invoice_url?: string
  invoice_pdf?: string
}

export interface PaymentMethod {
  id: string
  type: string
  card?: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  }
  is_default: boolean
  created?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────

function parseLim(v: { current?: number; limit?: number | string } | undefined) {
  if (!v) return { current: 0, limit: 0 as number | 'unlimited' }
  const lim = v.limit === 'Unlimited' || v.limit === -1 ? 'unlimited' as const : Number(v.limit ?? 0)
  return { current: v.current ?? 0, limit: lim }
}

// ─── API client ──────────────────────────────────────────────────────

export const billingApi = {
  /** Raw plan limits from backend (nested shape) */
  getLimits: () =>
    http<PlanLimits>('/billing/limits', { method: 'GET' }),

  /** Stripe configuration (publishable key, price IDs) */
  getConfig: () =>
    http<StripeConfig>('/billing/config', { method: 'GET' }),

  /** Create a Stripe Checkout session */
  createCheckoutSession: (
    planTier: 'growth' | 'enterprise',
    quantity: number = 1,
    billingCadence: 'monthly' | 'yearly' = 'monthly',
  ) =>
    http<CreateCheckoutResponse>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        plan_tier: planTier,
        quantity,
        billing_cadence: billingCadence,
      }),
    }),

  /** Create a Stripe Billing Portal session */
  createPortalSession: () =>
    http<CreatePortalResponse>('/billing/portal', { method: 'POST' }),

  /** Cancel subscription (at period end by default) */
  cancelSubscription: (immediately: boolean = false) =>
    http<{ success: boolean; message: string }>('/billing/cancel', {
      method: 'POST',
      body: JSON.stringify({ immediately }),
    }),

  /** Get current subscription details */
  getCurrentSubscription: () =>
    http<CurrentSubscription>('/billing/subscription', { method: 'GET' }),

  /** Get usage metrics (transforms nested backend shape to flat UI shape) */
  getUsageMetrics: async (): Promise<UsageMetrics> => {
    const data = await http<PlanLimits>('/billing/limits', { method: 'GET' })
    const u = data.usage || {} as PlanLimits['usage']
    const prospects = parseLim(u.prospects)
    const campaigns = parseLim(u.sequences)
    const team = parseLim(u.users)
    const api = parseLim(u.ai_outreach_monthly)
    const skillExec = parseLim(u.skill_executions_daily)
    const chatMsg = parseLim(u.chat_messages_daily)

    return {
      prospects_contacted: prospects.current,
      prospects_limit: prospects.limit,
      campaigns_active: campaigns.current,
      campaigns_limit: campaigns.limit,
      team_members_count: team.current,
      team_members_limit: typeof team.limit === 'number' ? team.limit : 999,
      api_calls_this_month: api.current,
      api_calls_limit: api.limit,
      storage_used_mb: 0,
      storage_limit_mb: 0,
      skill_executions_today: skillExec.current,
      skill_executions_daily_limit: skillExec.limit,
      chat_messages_today: chatMsg.current,
      chat_messages_daily_limit: chatMsg.limit,
    }
  },

  /** Get daily usage counters (skill executions + chat messages) */
  getDailyUsage: () =>
    http<{ skill_executions_daily: UsageInfo; chat_messages_daily: UsageInfo }>('/billing/daily-usage', { method: 'GET' }),

  /** Get available billing plans with experiment metadata */
  getAvailablePlansWithExperiment: async (variant?: string): Promise<AvailablePlansResponse> => {
    const query = variant ? `?variant=${encodeURIComponent(variant)}` : ''
    const data = await http<AvailablePlansResponse | BillingPlan[]>(`/billing/plans${query}`, { method: 'GET' })

    if (Array.isArray(data)) {
      return { plans: data }
    }
    return {
      plans: data.plans ?? [],
      currency: data.currency,
      trial_period_days: data.trial_period_days,
      enterprise_enabled: data.enterprise_enabled,
      marketing_copy: data.marketing_copy,
      experiment: data.experiment,
    }
  },

  /** Get available billing plans */
  getAvailablePlans: async (variant?: string): Promise<BillingPlan[]> => {
    const payload = await billingApi.getAvailablePlansWithExperiment(variant)
    return payload.plans
  },

  /** Create checkout session for plan change (alias for createCheckoutSession) */
  changePlan: (
    planId: string,
    quantity: number = 1,
    billingCadence: 'monthly' | 'yearly' = 'monthly',
  ) =>
    http<{ success: boolean; checkout_url?: string; message?: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        plan_tier: planId,
        quantity,
        billing_cadence: billingCadence,
      }),
    }),

  /** Get invoice history */
  getInvoices: async (limit: number = PAGINATION.INVOICES_PAGE_SIZE): Promise<Invoice[]> => {
    const data = await http<{ invoices?: Invoice[] } & Invoice[]>(
      `/billing/invoices?limit=${limit}`,
      { method: 'GET' },
    )
    return (data as { invoices?: Invoice[] }).invoices ?? data as unknown as Invoice[]
  },

  /** Get saved payment methods */
  getPaymentMethods: async (): Promise<PaymentMethod[]> => {
    const data = await http<{ payment_methods?: PaymentMethod[] } & PaymentMethod[]>(
      '/billing/payment-methods',
      { method: 'GET' },
    )
    return (data as { payment_methods?: PaymentMethod[] }).payment_methods ?? data as unknown as PaymentMethod[]
  },


  /** Server-side verification of a checkout redirect. The URL's success flag is
   *  attacker-typed; only this answer is allowed to drive a success claim. */
  confirmCheckoutSession: (sessionId: string) =>
    http<{ verified: boolean; status: string; payment_status: string; livemode: boolean; plan_tier: string | null }>(
      `/billing/checkout-session/${encodeURIComponent(sessionId)}`,
      { method: 'GET' },
    ),

  /** Open Stripe billing portal */
  openBillingPortal: () =>
    http<{ success: boolean; portal_url: string }>('/billing/portal', { method: 'POST' }),

  /* updateSeats removed (ADR-0046). The plan is flat per organisation, so there
     is no seat quantity to change. The SERVER endpoint is deliberately kept: any
     tenant still on a legacy multi-seat subscription must remain adjustable, and
     removing the route would strand them. What is gone is the client's ability
     to call something that no longer affects the bill. */
}

export default billingApi
